<template>
  <div
    v-if="visible"
    class="wallet-chip"
    role="link"
    tabindex="0"
    :title="isAdmin ? $t('walletChip.tooltipAdmin') : $t('walletChip.tooltip')"
    @click="goToWallet"
    @keydown.enter="goToWallet"
  >
    <span class="wallet-chip__icon">
      <feather-icon
        icon="PocketIcon"
        size="18"
      />
    </span>

    <span class="wallet-chip__text">
      <span class="wallet-chip__label d-none d-sm-block">
        {{ isAdmin ? $t('walletChip.labelAdmin') : $t('walletChip.label') }}
      </span>
      <span class="wallet-chip__amount">{{ displayAmount }}</span>
    </span>

    <button
      type="button"
      class="wallet-chip__eye"
      :aria-label="hidden ? $t('walletChip.show') : $t('walletChip.hide')"
      :title="hidden ? $t('walletChip.show') : $t('walletChip.hide')"
      @click.stop="toggleHidden"
      @keydown.enter.stop
    >
      <feather-icon
        :icon="hidden ? 'EyeOffIcon' : 'EyeIcon'"
        size="16"
      />
    </button>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { isAdminInCustomerContext } from '@/navigation/customerContext'

const HIDDEN_KEY = 'walletBalanceHidden'

export default {
  data() {
    return {
      loaded: false,
      failed: false,
      hidden: this.readHiddenPreference(),
    }
  },

  computed: {
    ...mapGetters('auth', ['currentUser']),
    ...mapGetters('wallet', ['walletBalance']),
    ...mapGetters('customers', ['selectedCustomerResourceId']),

    isCustomer() {
      return this.currentUser?.role_name === 'customer'
    },

    isAdmin() {
      return this.currentUser?.role_name === 'admin'
    },

    adminInCustomerContext() {
      return this.isAdmin && isAdminInCustomerContext(this.$route.name, this.$route.params)
    },

    customerId() {
      if (this.adminInCustomerContext) {
        return this.$route.params.customerId || this.selectedCustomerResourceId || null
      }

      const roles = Array.isArray(this.currentUser?.scoped_roles) ? this.currentUser.scoped_roles : []
      const role = roles.find(r => r.role_name === 'customer' && r.role_resource_type === 'Customer')
        || roles.find(r => r.role_resource_type === 'Customer')
        || roles[0]

      return role?.role_resource_id || null
    },

    canShow() {
      return this.isCustomer || this.adminInCustomerContext
    },

    visible() {
      return this.canShow && this.customerId && this.loaded && !this.failed
    },

    displayAmount() {
      if (this.hidden) return '$ ••••'

      return `$${this.$options.filters.money(this.walletBalance)}`
    },
  },

  watch: {
    '$route.fullPath': 'load',
    canShow: 'load',
    visible: {
      immediate: true,
      handler(value) {
        this.$emit('visibility-change', Boolean(value))
      },
    },
  },

  created() {
    this.load()
  },

  methods: {
    async load() {
      if (!this.canShow || !this.customerId) return

      try {
        if (this.adminInCustomerContext) {
          await this.$store.dispatch('wallet/fetchWalletByCustomer', { customerId: this.customerId })
        } else {
          await this.$store.dispatch('wallet/fetchWalletMe')
        }
        this.failed = false
      } catch (error) {
        this.failed = true
      } finally {
        this.loaded = true
      }
    },

    goToWallet() {
      if (this.$route.name === 'WalletForCustomer') return

      this.$router.push({ name: 'WalletForCustomer', params: { customerId: this.customerId } })
    },

    toggleHidden() {
      this.hidden = !this.hidden
      this.persistHiddenPreference()
    },

    persistHiddenPreference() {
      try {
        localStorage.setItem(HIDDEN_KEY, this.hidden ? '1' : '0')
        return true
      } catch (error) {
        return false
      }
    },

    readHiddenPreference() {
      try {
        return localStorage.getItem(HIDDEN_KEY) === '1'
      } catch (error) {
        return false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.wallet-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  height: 2.6rem;
  padding: 0 0.5rem 0 0.7rem;
  margin-right: 1rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.22);
  color: #fff;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
  user-select: none;

  &:hover,
  &:focus-visible {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.4);
    outline: none;
  }
}

.wallet-chip__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.9rem;
  height: 1.9rem;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.16);
}

.wallet-chip__text {
  display: flex;
  flex-direction: column;
  line-height: 1.15;
}

.wallet-chip__label {
  font-size: 0.66rem;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  opacity: 0.78;
}

.wallet-chip__amount {
  font-size: 0.95rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.2px;
}

.wallet-chip__eye {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.8rem;
  height: 1.8rem;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: inherit;
  opacity: 0.8;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background: rgba(255, 255, 255, 0.18);
    opacity: 1;
    outline: none;
  }
}

@media (max-width: 575.98px) {
  .wallet-chip {
    gap: 0.4rem;
    height: 2.3rem;
    margin-right: 0.5rem;
    padding: 0 0.35rem 0 0.5rem;
  }

  .wallet-chip__amount {
    font-size: 0.85rem;
  }
}
</style>
